/**
 * Chunk-load error recovery — the one implementation, shared by every SPA.
 *
 * A rebuild emits new hashed chunk filenames and ships a clean `dist/`, so the
 * previous build's chunks are GONE. Any document still referencing the old
 * names — a tab open across the deploy, or a stale cached `index.html` — gets a
 * 404 on its next dynamic import and the route fails to blank (the 2026-08-13
 * portal white screens). Recovery is a full reload, which re-fetches
 * `index.html` and with it the current chunk names.
 *
 * DEPENDENCY-FREE ON PURPOSE. Imported through the
 * `@claimshield/shared/router/chunk-reload` subpath by apps that must not pull
 * the shared barrel: driver-portal is a standalone rewards SPA with no
 * Vue-component dependency on shared, and adding one to get these functions
 * would drag PrimeVue and the shared stores into its bundle.
 *
 * Previously this logic existed twice — `shared/src/router/guards.ts` and
 * `marketing/src/router/index.ts` — with the shared copy carrying two holes the
 * marketing copy had already fixed (Chrome-only matching, no loop protection).
 * That duplication is exactly how the shared copy stayed broken; hence one home.
 */

/**
 * Query parameter carrying the attempt number of an in-flight recovery.
 *
 * It does two jobs at once, and the second is why it exists rather than a
 * plain sessionStorage counter:
 *
 *  1. CACHE BUSTER. A reload to the same URL is served from the same cache
 *     entry, so a document whose `index.html` was heuristically cached before
 *     the no-store fix landed reloaded straight back into the identical broken
 *     copy — the loop that needed a manual browser-cache clear to escape.
 *     A different URL is a different cache key, so the reload cannot be
 *     answered from the poisoned entry.
 *  2. STORAGE-INDEPENDENT ATTEMPT COUNTER. It survives the reload in the URL
 *     itself, so the loop stays bounded even where sessionStorage throws
 *     (Safari private browsing, "block all cookies", enterprise policy). A
 *     counter that lives only in blocked storage always reads back as zero,
 *     which is an unbounded reload loop dressed up as loop protection.
 */
export const CHUNK_RELOAD_MARKER = '_csr'

const RELOAD_STATE_KEY = 'cs:chunk-reload-state'
const RELOAD_WINDOW_MS = 30_000
const MAX_RELOAD_ATTEMPTS = 2

/**
 * Where a recovery is recorded — the same Sentinel endpoint the global error
 * capture posts to (`plugins/errorCapture.ts`), called by hand rather than by
 * importing it, because that module drags in Pinia and vue-router and this one
 * is dependency-free so driver-portal can use it without the shared barrel.
 */
const RECOVERY_REPORT_URL = import.meta.env.DEV
  ? '/api/v1/frontend-errors'
  : 'https://api.claimshieldpro.com/api/v1/frontend-errors'

/**
 * The production hosts whose recoveries are real events. Anywhere else — a developer
 * on `localhost`, a Playwright run, a `*.pages.dev` preview — reports nothing.
 *
 * WHY THIS EXISTS. `portal/tests/e2e/chunk-reload.spec.ts` (which PREDATES the
 * counter) drives the real `attemptChunkReload('/probe')` in a real browser, so
 * adding reporting inside this function turned every run of the portal e2e
 * suite into a write against production `system_logs`. Measured over the
 * counter's first thirteen days: 42 rows from `localhost`, 1 author
 * verification, 11 from a real user on a real domain. Reading the raw count
 * would have overstated the event rate roughly fivefold — in the direction that
 * manufactures urgency for work the evidence does not support. It also
 * manufactured all eleven `chunk_reload_exhausted` rows, which is the type that
 * would alert if it is ever made to.
 *
 * AN ALLOWLIST, NOT A `localhost` DENYLIST, and the trade is deliberate. A
 * denylist re-opens the same bug on the first host nobody thought to name (a
 * preview deployment, a tunnel, a new staging hostname). An allowlist instead
 * fails SILENT: an SPA that gains a host and is not added here reports zero and
 * reads as "no failures" — `network.claimshieldpro.com` is already provisioned
 * and would land in exactly that state the day it wires `setupGuards`. That
 * failure is survivable only because every message echoes
 * `window.location.host`, so a reader can always see which hosts are present.
 * **Adding an SPA host means adding a row here.**
 */
const REPORTING_HOSTS = new Set([
  'portal.claimshieldpro.com',
  'apply.claimshieldpro.com',
  'ops.claimshieldpro.com',
  'rewards.claimshieldpro.com',
  'driver.claimshieldpro.com',
  'mechanic.claimshieldpro.com',
])

/**
 * Record that a recovery fired, so the open-tab question rests on a number.
 *
 * WHAT THIS CLOSES. Until now the recovery path was the unmeasured one. A
 * chunk failure that reaches `router.onError` is rescued here and never
 * reaches the global error capture, so `system_logs` only ever held the
 * failures that ESCAPED recovery — component-level lazy imports surfacing as
 * `js_exception` / `unhandled_rejection`. Every one of those rows is a real
 * event, but they are the tail, not the distribution, and reading them as the
 * rate undercounts by however much the router catches. That gap is why
 * `proposals/2026-08-14-chunk-retention-across-deploys.md` could argue the
 * open-tab case but not price it.
 *
 * A COUNTER, NOT AN ALARM — and `error_type` is what enforces that. The
 * backend (`api/v1/frontend_errors.py` → `_should_alert_sync`) fires a Cliq
 * alert for every `js_exception` and `unhandled_rejection`; an unrecognised
 * type with no HTTP status falls through to the network branch, matches no
 * critical endpoint prefix, and returns False. The row still lands in
 * `system_logs`. Portal rebuilds 10-15x a day, so a type that alerted would
 * bury #sentinel-alerts inside a week.
 *
 * `keepalive`, not a bare fetch: `window.location.replace()` runs microseconds
 * after the reloading call and would cancel an ordinary request. `fetch`
 * rather than `navigator.sendBeacon`: the endpoint is cross-origin from every
 * SPA host, and a beacon carrying `application/json` needs a preflight it
 * cannot make, so it would fail silently — worse than not measuring at all.
 * errorCapture already proves this exact call crosses from all three hosts.
 *
 * No `user_email`: reading it means the auth store, and the import ban above
 * is worth more than knowing which consultant was on the stale tab. The count
 * is the question being asked.
 */
function reportRecovery(errorType: 'chunk_reload' | 'chunk_reload_exhausted', message: string): void {
  try {
    // Before anything else: a test browser is not a user. See REPORTING_HOSTS.
    if (!REPORTING_HOSTS.has(window.location.hostname)) return

    void fetch(RECOVERY_REPORT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        error_message: message.slice(0, 2000),
        error_type: errorType,
        http_status: null,
        failed_endpoint: null,
        http_method: null,
        user_email: null,
        page_path: window.location.pathname,
        timestamp: new Date().toISOString(),
      }),
      keepalive: true,
    }).catch(() => {
      // Fire-and-forget. A rejected telemetry POST must not surface as an
      // unhandled rejection: errorCapture would report THAT, turning a missed
      // measurement into a Cliq alert.
    })
  } catch {
    // Telemetry must never break the path whose job is to rescue a white screen.
  }
}

/**
 * Every engine phrases a failed dynamic import differently, and matching only
 * one of them is why the original guard recovered on Chrome and left Safari and
 * Firefox users on a white screen.
 *   Chrome  — "Failed to fetch dynamically imported module"
 *   Safari  — "Importing a module script failed"
 *   Firefox — "error loading dynamically imported module"
 */
const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError/i

/** Message-only test, for `window.onerror` / `unhandledrejection` backstops. */
export function isChunkLoadErrorMessage(msg: string): boolean {
  return CHUNK_ERROR_PATTERN.test(msg)
}

/** A dynamic-import failure for a chunk that no longer exists, all engines. */
export function isChunkLoadError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error)
  return isChunkLoadErrorMessage(msg) || (error as { name?: string })?.name === 'ChunkLoadError'
}

interface ReloadState {
  count: number
  firstAt: number
}

/**
 * Attempt number recorded in the current URL. Survives reloads, never throws.
 *
 * The marker is `<attempt>.<epochMs>` and EXPIRES on the same window as the
 * storage counter. The timestamp is not decoration: without it the URL counter
 * is immortal, and a URL captured mid-incident — bookmarked, pasted into Cliq,
 * restored by "reopen tabs on startup", or kept in history — carries `_csr=2`
 * forever. Weeks later, a first, unrelated chunk failure on that URL computes
 * attempt 3, exceeds the budget immediately, and refuses to reload: the user
 * sits on a white screen that one reload would have fixed, and the recovery
 * reports itself exhausted having never run once. Found by adversarial review
 * (2026-08-14), two independent finders with executed reproductions.
 *
 * A bare legacy `_csr=2` with no timestamp is treated as expired for the same
 * reason — it cannot be dated, so it must not be trusted to bound anything.
 */
function attemptFromUrl(now: number): number {
  try {
    const raw = new URLSearchParams(window.location.search).get(CHUNK_RELOAD_MARKER)
    if (!raw) return 0
    const [attemptPart = '', stampPart = ''] = raw.split('.')
    const attempt = Number.parseInt(attemptPart, 10)
    const stamp = Number.parseInt(stampPart, 10)
    if (!Number.isFinite(attempt) || attempt <= 0) return 0
    if (!Number.isFinite(stamp) || now - stamp > RELOAD_WINDOW_MS) return 0
    return attempt
  } catch {
    return 0
  }
}

/**
 * Attempt number in sessionStorage. Secondary to the URL marker: it bounds
 * repeated failures WITHIN one document (several lazy imports failing before
 * any reload happens), which the URL cannot see. Returns 0 when storage is
 * blocked or the window has expired.
 */
function readStorageState(now: number): ReloadState | null {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(RELOAD_STATE_KEY) || '') as ReloadState | null
    if (!parsed || typeof parsed !== 'object') return null
    if (typeof parsed.firstAt !== 'number' || now - parsed.firstAt > RELOAD_WINDOW_MS) return null
    if (typeof parsed.count !== 'number' || parsed.count < 1) return null
    return parsed
  } catch {
    return null
  }
}

function attemptFromStorage(now: number): number {
  return readStorageState(now)?.count ?? 0
}

function persistAttempt(attempt: number, now: number): void {
  try {
    const firstAt = readStorageState(now)?.firstAt ?? now
    sessionStorage.setItem(RELOAD_STATE_KEY, JSON.stringify({ count: attempt, firstAt }))
  } catch {
    // Storage blocked. Deliberately swallowed: the URL marker above is the
    // authoritative counter, so recovery stays bounded without this write.
    // (Previously this write sat outside any try/catch and threw straight out
    // of router.onError, so the one path whose job is to rescue a white screen
    // failed closed on exactly the browsers most likely to block storage.)
  }
}

/**
 * Builds the reload target, stamping `<attempt>.<now>` into the query so the
 * counter both busts the cache and can be dated (see attemptFromUrl).
 */
function withMarker(target: string, attempt: number, now: number): string {
  try {
    const url = new URL(target, window.location.origin)
    url.searchParams.set(CHUNK_RELOAD_MARKER, `${attempt}.${now}`)
    // pathname+search+hash only: an absolute URL resolves against our own
    // origin and is then reduced to a same-origin path, so a hostile or
    // mistaken target can never turn recovery into an open redirect.
    return url.pathname + url.search + url.hash
  } catch {
    return target
  }
}

/**
 * Recover from a chunk-load failure by reloading, at most
 * {@link MAX_RELOAD_ATTEMPTS} times per incident.
 *
 * Returns `false` when the budget is spent — the caller has already logged the
 * underlying error, and stopping leaves a console error rather than a reload
 * loop. Callers pass the route they were navigating to; omit it to reload the
 * current URL (the `window`-level backstops, which have no route context).
 */
export function attemptChunkReload(targetPath?: string): boolean {
  const now = Date.now()
  const attempt = Math.max(attemptFromUrl(now), attemptFromStorage(now)) + 1

  // `router` when a navigation handed us the route it was going to, `window`
  // when a window-level backstop caught a lazy import fired outside the
  // router. Which population dominates decides whether the open-tab fix
  // belongs in the router or somewhere else entirely.
  const origin = targetPath ? 'router' : 'window'

  if (attempt > MAX_RELOAD_ATTEMPTS) {
    console.error(
      `[ChunkReload] ${MAX_RELOAD_ATTEMPTS} recovery reloads already attempted; ` +
        'not reloading again. The page is stale and needs a manual refresh.',
    )
    // The one genuinely user-visible outcome: recovery gave up and somebody is
    // sitting on a broken page. Counted separately from a reload that worked.
    reportRecovery(
      'chunk_reload_exhausted',
      `[ChunkReload] exhausted after ${MAX_RELOAD_ATTEMPTS} reloads (${origin}) ` +
        `on ${window.location.host}${targetPath || window.location.pathname} — page still stale`,
    )
    return false
  }

  persistAttempt(attempt, now)
  const target = targetPath || window.location.pathname + window.location.search
  reportRecovery(
    'chunk_reload',
    `[ChunkReload] attempt ${attempt}/${MAX_RELOAD_ATTEMPTS} (${origin}) ` +
      `on ${window.location.host} -> ${target}`,
  )
  // `replace`, not `href` — the broken document must not become a history
  // entry the back button can return to.
  window.location.replace(withMarker(target, attempt, now))
  return true
}

/**
 * Clear the recovery marker once a navigation has actually succeeded.
 *
 * Called from `router.afterEach`: a resolved route means the chunk loaded, so
 * the incident is over and the next one starts its own budget. Returns the
 * cleaned query when the marker was present (the caller does the
 * `router.replace`, keeping this module free of a vue-router import), or
 * `null` when there was nothing to clean.
 */
export function clearedChunkReloadQuery<T extends Record<string, unknown>>(query: T): T | null {
  if (!(CHUNK_RELOAD_MARKER in query)) {
    return null
  }
  try {
    sessionStorage.removeItem(RELOAD_STATE_KEY)
  } catch {
    // Storage blocked — nothing to clear.
  }
  // Generic in the query type so callers keep vue-router's LocationQuery and
  // this module keeps its zero-dependency import surface.
  const cleaned = { ...query } as Record<string, unknown>
  delete cleaned[CHUNK_RELOAD_MARKER]
  return cleaned as T
}
